import HeroSection from "./components/HeroSection";
import ImmediateQualifierSection from "./components/ImmediateQualifierSection";
import HowItWorksSection from "./components/HowItWorksSection";
import EmergencyTiersSection from "./components/EmergencyTiersSection";
import CommonSituationsSection from "./components/CommonSituationsSection";
import WhyFastPassportCenterSection from "./components/WhyFastPassportCenterSection";
import WhatWontWorkSection from "./components/WhatWontWorkSection";
import FAQSection from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";
import RelatedPassportServices from "@/components/feature/RelatedPassportServices";

const pageUrl = "https://fastpassportcenter.com/passport-services/emergency-passport/";

const faqSchemaItems = [
  {
    "@type": "Question",
    name: "Can I really get a passport in 24 hours?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes — but only under specific conditions. Your documents must be received by our courier the same or previous business day, your situation must qualify for emergency processing, and the passport agency must have capacity for same-day processing. When these conditions are met, Fast Passport Center can submit your application same-day and receive the passport back by overnight FedEx — putting it in your hands within 24 hours of agency release. Call us at 877-253-0084 to assess your specific situation.",
    },
  },
  {
    "@type": "Question",
    name: "Can I walk into a passport agency without an appointment?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "No. Regional passport agencies in the U.S. are strictly appointment-only. In 2026, appointments typically book 2–4 weeks in advance. There is no walk-in service at any U.S. regional passport agency. This is why registered couriers like Fast Passport Center exist — we have reserved daily submission slots that bypass the public appointment system entirely.",
    },
  },
  {
    "@type": "Question",
    name: "What's the difference between emergency and expedited passport service?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Expedited service generally refers to processing in 3–9 business days using a registered courier's reserved agency slots — faster than the government's standard 4–6 week timeline. Emergency service refers to situations where travel is within 72 hours and same-day or next-day processing is needed. Fast Passport Center handles both — the right tier depends on your specific travel date.",
    },
  },
  {
    "@type": "Question",
    name: "What if I just lost my passport and my trip is in 3 days?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Call us immediately at 877-253-0084. For a lost passport emergency, you'll need to file Form DS-64 (lost passport report) and apply for a new passport using Form DS-11 in person at a passport acceptance facility. We'll guide you through the exact steps, tell you where to go for the DS-11 acceptance step, and then our courier handles the agency submission. It's tight but often doable in 3 business days.",
    },
  },
  {
    "@type": "Question",
    name: "Do I have to go to a passport agency myself?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "For passport renewals (DS-82), no — you never need to visit a passport agency or acceptance facility. We handle everything. For new passports and lost passport replacements (DS-11), you must visit a passport acceptance facility (post office, courthouse, or library that accepts passport applications) for the in-person signature step. You do NOT need to go to a regional passport agency yourself — that's our job.",
    },
  },
  {
    "@type": "Question",
    name: "What if I'm traveling in less than 48 hours?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Call us immediately at 877-253-0084. A 48-hour window is extremely tight but sometimes possible depending on what day it is, what type of passport you need, and whether your documents are ready. We will give you an honest assessment in minutes. Do not waste time — call now.",
    },
  },
  {
    "@type": "Question",
    name: "What documents do I need for an emergency passport?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "It depends on your situation. For an emergency renewal (DS-82): current passport, passport photo, proof of travel, and Form DS-82. For an emergency new passport (DS-11): birth certificate, government ID, passport photo, proof of travel, and Form DS-11 (signed at an acceptance facility). For a lost/stolen replacement: DS-64 (lost report), DS-11, birth certificate, government ID, passport photo, and proof of travel. We provide a personalized checklist when you call or start your order.",
    },
  },
  {
    "@type": "Question",
    name: "How much does emergency passport service cost?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Our service fees vary by processing speed. The faster you need it, the higher the service fee. Government fees are separate and required regardless of which service you use. Call 877-253-0084 or visit our checkout for current pricing. We are typically 20–80% less expensive than retail passport counters like FedEx Office or Staples for the same emergency service.",
    },
  },
  {
    "@type": "Question",
    name: "What if my passport application is already in process but my trip is coming up?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Call us at 877-253-0084. If you have an active application that isn't moving fast enough for your travel date, there may be options — including escalation through our agency contacts or, in some cases, withdrawing and resubmitting through our courier service on an emergency basis. We'll assess your situation and tell you honestly what's possible.",
    },
  },
  {
    "@type": "Question",
    name: "Is Fast Passport Center on the official State Department list of registered couriers?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes. Fast Passport Center has been a registered U.S. Department of State passport courier for over 20 years and appears on the official government registry at travel.state.gov. You can verify this directly on the State Department's website. We strongly recommend always checking this registry before using any passport expediting service.",
    },
  },
];

const serviceSchema = {
  "@type": "Service",
  name: "Emergency Passport Service",
  provider: {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "1-877-253-0084",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
    },
  },
  description: "Emergency passport processing as fast as 24 hours from a registered US Department of State passport courier with daily reserved agency access.",
  areaServed: "US",
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    description: "Emergency passport service — call for pricing based on urgency and timeline",
  },
};

const breadcrumbSchema = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com" },
    { "@type": "ListItem", position: 2, name: "Passport Services", item: "https://fastpassportcenter.com/passport-services/" },
    { "@type": "ListItem", position: 3, name: "Emergency Passport", item: pageUrl },
  ],
};

const graph = [
  {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "+1-877-253-0084",
    email: "contactus@fastpassportcenter.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
      addressCountry: "US",
    },
    areaServed: { "@type": "Country", name: "United States" },
    priceRange: "$$$",
    openingHours: ["Mo-Fr 09:00-18:00"],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
  },
  serviceSchema,
  breadcrumbSchema,
  { "@type": "FAQPage", mainEntity: faqSchemaItems },
];

const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

function EmergencyPassportSEOHead() {
  return (
    <>
      <title>Emergency Passport Service — As Fast as 24 Hours | Fast Passport Center</title>
      <meta name="description" content="Need an emergency passport? Fast Passport Center is a registered State Dept courier with daily agency access. Emergency passport processing as fast as 24 hours. Call 877-253-0084 now — we answer fast." />
      <meta name="keywords" content="emergency passport, emergency passport service, how to get an emergency passport, emergency passport same day, I need a passport emergency, passport emergency travel, emergency passport courier" />
      <meta name="last-modified" content="2026-07-01" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Emergency Passport Service | Fast Passport Center" />
      <meta property="og:description" content="Emergency passport processing as fast as 24 hours. Registered State Dept courier with daily reserved agency access. Call 877-253-0084." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Fast Passport Center" />
      <meta property="og:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Emergency Passport Service | Fast Passport Center" />
      <meta name="twitter:description" content="Emergency passport processing as fast as 24 hours. Registered State Dept courier with daily reserved agency access. Call 877-253-0084." />
      <meta name="twitter:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <link rel="canonical" href={pageUrl} />
      <script type="application/ld+json" id="structured-data-emergency-passport" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </>
  );
}

export default function EmergencyPassportPage() {
  return (
    <div className="font-sans">
      <EmergencyPassportSEOHead />
      <HeroSection />
      <ImmediateQualifierSection />
      <HowItWorksSection />
      <EmergencyTiersSection />
      <CommonSituationsSection />
      <WhyFastPassportCenterSection />
      <WhatWontWorkSection />
      <FAQSection />

      <RelatedPassportServices
        services={[
          {
            title: "Urgent Passport Service",
            description: "Trip 3-14 days out? Urgent processing with a bit more breathing room. From 3 business days.",
            link: "/passport-services/urgent-passport/",
            icon: "ri-timer-line",
          },
          {
            title: "Fast Passport Renewal",
            description: "Not an emergency but need it fast? Renew by mail with no office visit. As fast as 3 days.",
            link: "/passport-services/fast-passport-renewal/",
            icon: "ri-refresh-line",
          },
          {
            title: "Expedited Passport Service",
            description: "Broader overview of all speed tiers and processing options. Compare side-by-side.",
            link: "/passport-services/expedited-passport-service/",
            icon: "ri-speed-up-line",
          },
          {
            title: "Passport Expediting Service",
            description: "Verify our State Dept credentials. Direct courier — not a reseller. 20+ years registered.",
            link: "/passport-services/passport-expediting-service/",
            icon: "ri-government-line",
          },
        ]}
        colorClass="text-[#8B0000]"
        bgClass="bg-[#FFF0F0]"
        borderClass="border-[#F5D5D5]"
        linkColorClass="text-[#8B0000]"
      />

      <FinalCTASection />

      {/* Footer */}
      <footer className="bg-[#0F1E3D] text-gray-300 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Fast Passport Center</h4>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Registered U.S. Department of State passport courier. A+ BBB rated. 20+ years of passport and visa experience.
              </p>
              <p className="text-gray-500 text-xs">
                1629 K Street, Suite 300<br />Washington, DC 20006
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/passport-services/emergency-passport/" className="hover:text-white transition-colors cursor-pointer">Emergency Passport</a></li>
                <li><a href="/passport-services/urgent-passport/" className="hover:text-white transition-colors cursor-pointer">Urgent Passport</a></li>
                <li><a href="/passport-services/fast-passport-renewal/" className="hover:text-white transition-colors cursor-pointer">Fast Passport Renewal</a></li>
                <li><a href="/passport-services/expedited-passport-service/" className="hover:text-white transition-colors cursor-pointer">Expedited Passport Service</a></li>
                <li><a href="/passport-services/new-passport/" className="hover:text-white transition-colors cursor-pointer">New Passport</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="tel:8772530084" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">877-253-0084</a></li>
                <li><a href="mailto:contactus@fastpassportcenter.com" className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><a href="/helpful-resources/" className="hover:text-white transition-colors cursor-pointer">Helpful Resources</a></li>
                <li><a href="/helpful-resources/passport-processing-times/" className="hover:text-white transition-colors cursor-pointer">Processing Times</a></li>
                <li><a href="/helpful-resources/passport-fees/" className="hover:text-white transition-colors cursor-pointer">Passport Fees</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Trust &amp; Credentials</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line"></i> A+ BBB Accredited
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-government-fill"></i> Registered State Dept. Courier
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-history-line"></i> 20+ Years Experience
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-lock-line"></i> Secure &amp; Confidential
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#3A4A6B] pt-8 text-center text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Fast Passport Center. All rights reserved. Fast Passport Center is a private passport courier service registered with the U.S. Department of State. We are not a government agency.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}